<template>
  <v-container fluid class="ma-0 pa-0">
    <v-row class="ma-0 pa-0 mt-4">
      <v-col cols="12" class="ma-0 pa-0">
        <variable-toolbar
          :predictor-count="selectedPredictors.length"
          :predictor-total="availablePredictors.length"
          :outcome-count="selectedOutcomes.length"
          :outcome-total="availableOutcomes.length"
          :context-label="contextLabel"
          @openPredictors="predictorDialog = true"
          @openOutcomes="outcomeDialog = true"
          @clear="clearSelection"
          @reset="resetSelection"
        />
      </v-col>
    </v-row>

    <v-row v-if="dashboardVariables.length === 0" class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0">
        <div
          class="display-1 primary--text text--lighten-5 pt-5 mt-5"
          align="center"
        >
          NO VARIABLES AVAILABLE
        </div>
      </v-col>
    </v-row>

    <v-row v-else class="ma-0 pa-0 mt-3">
      <v-col cols="12" class="ma-0 pa-0">
        <v-card class="ma-0 pa-3">
          <div class="d-flex align-baseline mb-1">
            <span class="text-h6">1 &middot; Variable summary</span>
            <v-spacer />
            <span class="dd-caption">
              one card per selected variable &mdash; distribution, n,
              missingness
            </span>
          </div>

          <div class="dd-caption mb-3">
            Click any card for a full-size plot.
          </div>

          <template v-for="group in summaryGroups">
            <div :key="group.key + '-head'" class="d-flex align-baseline mt-2">
              <span class="text-subtitle-1 font-weight-bold">
                {{ group.title }}
              </span>
              <span class="dd-caption ml-2">{{ group.hint }}</span>
            </div>

            <div
              v-if="group.variables.length === 0"
              :key="group.key + '-empty'"
              class="text-body-2 my-2"
            >
              {{ group.emptyMessage }}
            </div>

            <v-row v-else :key="group.key + '-grid'" class="ma-0 pa-0 mt-1">
              <v-col
                v-for="v in group.variables"
                :key="'dd-card-' + v.uid"
                cols="12"
                sm="6"
                md="4"
                lg="3"
                class="pa-2"
              >
                <summary-card
                  :variable="v"
                  :num-subjects="numSubjects"
                  :longitudinal="longitudinal"
                  @click="openChart"
                />
              </v-col>
            </v-row>
          </template>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="dashboardVariables.length > 0" class="ma-0 pa-0 mt-3">
      <v-col cols="12" class="ma-0 pa-0">
        <outcome-by-predictor
          :outcomes="selectedOutcomes"
          :predictors="selectedPredictors"
          :longitudinal="longitudinal"
          :context-label="contextLabel"
          :storage-key="storageKey"
        />
      </v-col>
    </v-row>

    <variable-picker-dialog
      v-model="predictorDialog"
      title="Choose Predictor Variables"
      :variables="availablePredictors"
      :selected-ids="selectedPredictorIds"
      @update:selectedIds="setPredictorIds"
    />

    <variable-picker-dialog
      v-model="outcomeDialog"
      title="Choose Outcome Variables"
      :variables="availableOutcomes"
      :selected-ids="selectedOutcomeIds"
      @update:selectedIds="setOutcomeIds"
    />

    <!-- same drill-down the Variables tab uses -->
    <v-dialog v-model="chartDialog" width="50%">
      <variable-summary-chart
        v-if="clickedVariable"
        :variable="clickedVariable"
        @close="chartDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import {
  isDashboardEligible,
  isPredictor,
  reconcileSelection,
  selectDefaultVariables,
  variableKey,
} from '@/utils/dashboard';
import { readStoredJson, writeStoredJson } from '@/utils/helpers';
import VariableSummaryChart from '@/components/DatasetSummary/VariableSummaryChart.vue';
import OutcomeByPredictor from '@/components/DatasetSummary/DataDashboard/OutcomeByPredictor.vue';
import SummaryCard from '@/components/DatasetSummary/DataDashboard/SummaryCard.vue';
import VariablePickerDialog from '@/components/DatasetSummary/DataDashboard/VariablePickerDialog.vue';
import VariableToolbar from '@/components/DatasetSummary/DataDashboard/VariableToolbar.vue';

// localStorage key prefix; scoped per dataset, since a uid from one dataset
// means nothing in another
const SELECTION_KEY_PREFIX = 'dashboard_selection';

export default {
  name: 'DataDashboard',
  components: {
    OutcomeByPredictor,
    SummaryCard,
    VariablePickerDialog,
    VariableSummaryChart,
    VariableToolbar,
  },
  data() {
    return {
      predictorDialog: false,
      outcomeDialog: false,
      chartDialog: false,
      clickedVariable: null,
      selectedPredictorIds: [],
      selectedOutcomeIds: [],
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      dataset: state.DATASET,
      collection: state.COLLECTION,
      isCollection: state.IS_COLLECTION,
      variables: state.VARIABLES,
    }),
    // study and collection carry the same information under different names
    subject() {
      return this.isCollection ? this.collection : this.dataset;
    },
    numSubjects() {
      return this.subject ? this.subject.num_subjects : 0;
    },
    // matches the collection-/study- scoping VariableTable.vue already uses
    storageKey() {
      if (!this.subject) return null;
      const scope = this.isCollection ? 'collection' : 'study';
      return `${SELECTION_KEY_PREFIX}-${scope}-${this.subject.id}`;
    },
    longitudinal() {
      return !!(this.subject && this.subject.longitudinal);
    },
    contextLabel() {
      const shape = this.longitudinal
        ? 'longitudinal · values at baseline'
        : 'cross-sectional';
      return `${this.numSubjects} subjects · ${shape}`;
    },
    // `uid` is attached once here so selection, keys, and table identity all
    // agree; a bare `id` collides between subject and observation variables
    dashboardVariables() {
      return this.variables
        .filter(isDashboardEligible)
        .map(v => ({ ...v, uid: variableKey(v) }));
    },
    availablePredictors() {
      return this.dashboardVariables.filter(isPredictor);
    },
    availableOutcomes() {
      return this.dashboardVariables.filter(v => !isPredictor(v));
    },
    selectedPredictors() {
      return this.pick(this.availablePredictors, this.selectedPredictorIds);
    },
    selectedOutcomes() {
      return this.pick(this.availableOutcomes, this.selectedOutcomeIds);
    },
    // The wireframe draws one flat grid, but the toolbar selects predictors
    // and outcomes separately, so the cards are grouped the same way
    summaryGroups() {
      return [
        {
          key: 'predictors',
          title: 'Predictors',
          hint: 'demographic and subject-level variables',
          variables: this.selectedPredictors,
          emptyMessage:
            this.availablePredictors.length === 0
              ? 'This dataset has no demographic or subject-level variables.'
              : 'No predictors selected.',
        },
        {
          key: 'outcomes',
          title: 'Outcomes',
          hint: 'measured variables',
          variables: this.selectedOutcomes,
          emptyMessage:
            this.availableOutcomes.length === 0
              ? 'This dataset has no outcome variables.'
              : 'No outcomes selected.',
        },
      ];
    },
  },
  watch: {
    // SET_VARIABLES is committed only on fetch, so this fires when the view
    // loads a different dataset while the tab stays mounted
    variables() {
      this.restoreSelection();
    },
  },
  created() {
    this.restoreSelection();
  },
  methods: {
    // preserve the available-list ordering rather than click order
    pick(available, ids) {
      const wanted = new Set(ids);
      return available.filter(v => wanted.has(v.uid));
    },
    openChart(v) {
      this.clickedVariable = v;
      this.chartDialog = true;
    },
    setPredictorIds(ids) {
      this.selectedPredictorIds = ids;
      this.persistSelection();
    },
    setOutcomeIds(ids) {
      this.selectedOutcomeIds = ids;
      this.persistSelection();
    },
    clearSelection() {
      this.selectedPredictorIds = [];
      this.selectedOutcomeIds = [];
      this.persistSelection();
    },
    resetSelection() {
      this.applyDefaults();
      this.persistSelection();
    },
    applyDefaults() {
      const { predictors, outcomes } = selectDefaultVariables(
        this.dashboardVariables
      );
      this.selectedPredictorIds = predictors.map(v => v.uid);
      this.selectedOutcomeIds = outcomes.map(v => v.uid);
    },
    // Only ever called for a dataset the user has not touched yet, so it must
    // not write anything back: persisting the defaults here would freeze this
    // dataset onto whatever the defaults happened to be on first visit.
    restoreSelection() {
      const stored = this.storageKey ? readStoredJson(this.storageKey) : null;
      if (!stored) {
        this.applyDefaults();
        return;
      }

      const predictors = reconcileSelection(
        stored.predictors,
        this.availablePredictors
      );
      const outcomes = reconcileSelection(
        stored.outcomes,
        this.availableOutcomes
      );

      // each list falls back independently, so a stored selection that has
      // gone stale on one side still restores the other
      this.applyDefaults();
      if (predictors !== null) this.selectedPredictorIds = predictors;
      if (outcomes !== null) this.selectedOutcomeIds = outcomes;
    },
    persistSelection() {
      if (!this.storageKey) return;
      writeStoredJson(this.storageKey, {
        predictors: this.selectedPredictorIds,
        outcomes: this.selectedOutcomeIds,
      });
    },
  },
};
</script>

<style scoped>
.dd-caption {
  font-size: 12px;
  color: #777;
}
</style>
